<script setup>
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import AiAgentKnowledgeSourcesAPI from 'dashboard/api/aiAgents/knowledgeSources';

const props = defineProps({
  agentId: { type: Number, required: true },
});

const { t } = useI18n();
const router = useRouter();
const { accountScopedRoute } = useAccount();
const sources = ref([]);
const isLoading = ref(false);

const loadSources = async () => {
  isLoading.value = true;
  try {
    const { data } = await AiAgentKnowledgeSourcesAPI.index(props.agentId);
    sources.value = data;
  } catch {
    useAlert(t('AI_AGENTS.KNOWLEDGE.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const onToggle = async source => {
  try {
    await AiAgentKnowledgeSourcesAPI.toggle(
      props.agentId,
      source.id,
      source.is_enabled
    );
    source.assigned = true;
  } catch {
    source.is_enabled = !source.is_enabled;
    useAlert(t('AI_AGENTS.API.ERROR'));
  }
};

const openKnowledgeSettings = (openAdd = false) => {
  router.push(
    accountScopedRoute(
      'ai_knowledge_sources_settings',
      {},
      openAdd ? { add: '1' } : {}
    )
  );
};

onMounted(loadSources);
</script>

<template>
  <section class="grid gap-4">
    <div
      class="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-n-amber-3 p-3 text-sm text-n-slate-12"
    >
      <div class="flex min-w-0 gap-3">
        <span class="mt-0.5 i-lucide-circle-alert shrink-0 text-n-amber-9" />
        <div>
          <p class="m-0 font-medium">
            {{ t('AI_AGENTS.KNOWLEDGE.INFO_TITLE') }}
          </p>
          <p class="m-0 mt-1 text-n-slate-11">
            {{ t('AI_AGENTS.KNOWLEDGE.AGENT_ENABLE_DESCRIPTION') }}
          </p>
        </div>
      </div>
      <Button
        :label="t('AI_AGENTS.ACTIONS.MANAGE_KNOWLEDGE_SOURCES')"
        icon="i-lucide-book-open"
        outline
        slate
        sm
        class="!bg-n-background shrink-0"
        @click="openKnowledgeSettings"
      />
    </div>

    <woot-loading-state
      v-if="isLoading"
      :message="t('AI_AGENTS.KNOWLEDGE.LOADING')"
    />

    <ul v-else-if="sources.length" class="m-0 grid list-none gap-2 p-0">
      <li
        v-for="source in sources"
        :key="source.id"
        class="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-n-weak p-3"
      >
        <div class="grid min-w-0 gap-1">
          <p class="m-0 text-sm font-medium text-n-slate-12">
            {{ source.name }}
          </p>
          <p class="m-0 truncate text-xs text-n-slate-11">
            {{
              source.source_type === 'file'
                ? source.file_name || t('AI_AGENTS.KNOWLEDGE.TYPE_FILE')
                : source.source_url
            }}
          </p>
        </div>
        <div class="flex items-center gap-2">
          <span class="text-xs text-n-slate-11">
            {{ t('AI_AGENTS.KNOWLEDGE.USE_FOR_AGENT') }}
          </span>
          <Switch
            v-model="source.is_enabled"
            size="md"
            @change="onToggle(source)"
          />
        </div>
      </li>
    </ul>

    <div v-else class="grid justify-items-center gap-3 py-6 text-center">
      <p class="m-0 text-sm text-n-slate-11">
        {{ t('AI_AGENTS.KNOWLEDGE.EMPTY_ACCOUNT') }}
      </p>
      <Button
        :label="t('AI_AGENTS.KNOWLEDGE.ADD_SOURCE')"
        icon="i-lucide-plus"
        outline
        slate
        sm
        @click="openKnowledgeSettings(true)"
      />
    </div>
  </section>
</template>
